import React, { useState } from 'react';

import { isAfter, isEqual } from 'date-fns';

import {
  Box,
  Button,
  ButtonGroup,
  Card,
  DateInput,
  DatePicker,
  FormField,
  FormHelp,
  Heading,
  Icon,
  Modal,
  Paragraph,
  Tag,
  Text,
  TextButton,
} from '@zillow/constellation-10';
import { IconCalendarFilled } from '@zillow/constellation-icons';
import type { ZrmClickstreamUser } from '@zg-rentals/clickstream';

import { coerceToDate } from '@common/paymentsUtils/dateUtils';
import RachelCSEvents from '@renter-hub/tracking/ClickStreamEvents';
import Page from '@renter-hub/components/RenterPage';
import HelmetContainer from '@common/components/HelmetContainer';
import SeoConstants from '@renter-hub/constants/seoConstants';
import faqs from '@common/constants/faqs';
import FaqComponent from '@common/components/ConstellationFaqComponent';
import NotificationContainer from '@common/components/NotificationContainer';
import DebouncedButton from '@common/components/ConstellationDebouncedButton';
import AvailablePaymentMethods from '../../components/AvailablePaymentMethods';
import userPersonas from '@common/constants/userPersonas';
import { formatDate, getLastSelectablePaymentDate } from '@common/paymentsUtils/dateUtils';
import Calendar from '@common/components/Calendar';
import type { RenterMoveInCostsDetailsInterface, RenterPaymentMethodInterface } from '@renter-hub/models/Types';
import TransactionConstants from '@common/constants/TransactionConstants';
import MoveInCostsItemsDisplay from '@renter-hub/components/MoveInCostsItemsInfo';
import { MOVE_IN_COSTS_PAYMENT } from '@renter-hub/constants/paymentConstants';
import { ScheduleMoveInCostsWebApiRequestDto } from '@zg-rentals/ts-rent-payment-client';

interface Props {
  today: Date;
  fromDashboard: boolean;
  moveInCostsDetails: RenterMoveInCostsDetailsInterface;
  onSubmit: (...properties: any) => any;
  togglePrevPage: () => void;
  goToDashboard: () => void;
  goToPaymentSettingPage: () => void;
  failedPaymentEmail: boolean;
  encodedPaymentMethodId: string;
  onAddNewPaymentClick: () => void;
  newPaymentMethodId: string;
  user: ZrmClickstreamUser;
  paymentMethods: Array<RenterPaymentMethodInterface>;
  listingAlias?: string;
}

const overDueText =
  "This is after the payment date your landlord chose. Make sure they're okay with getting the payment later.";

const MoveInCostsSchedulingPage = ({
  today = new Date(),
  fromDashboard = false,
  moveInCostsDetails,
  togglePrevPage,
  goToPaymentSettingPage,
  failedPaymentEmail = false,
  encodedPaymentMethodId = '',
  onAddNewPaymentClick,
  newPaymentMethodId,
  user,
  paymentMethods,
  listingAlias,
  ...props
}: Props) => {
  const [isLoading, setIsLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(
    moveInCostsDetails.isScheduled
      ? formatDate(moveInCostsDetails.sendDate, 'MM/dd/yyyy')
      : formatDate(moveInCostsDetails.dueDate, 'MM/dd/yyyy'),
  );
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [selectedPaymentMethodEncodedId, setSelectedPaymentMethodEncodedId] = useState(
    newPaymentMethodId || moveInCostsDetails.paymentMethodId || encodedPaymentMethodId,
  );
  const [showMultiplePaymentMethodSelectorModal, setShowMultiplePaymentMethodSelectorModal] = useState(true);

  const changeSelectedDate = (newSelectedDate: string) => {
    setSelectedDate(formatDate(newSelectedDate, 'MM/dd/yyyy'));
  };

  const isMICPaid = moveInCostsDetails.status === TransactionConstants.STATUS.COMPLETE;

  const isOverdue = () => {
    const left = coerceToDate(today);
    const right = coerceToDate(moveInCostsDetails.dueDate);
    return !!left && !!right ? isAfter(left, right) : false;
  };

  const isScheduleDateUnchanged = () => {
    const { sendDate, isScheduled } = moveInCostsDetails;
    return isScheduled && sendDate === selectedDate;
  };

  const goToDashboard = () => {
    RachelCSEvents.emitClickGoToDashboardEvent({
      user,
      currentProperty: { listingAlias },
      triggerObject: 'move_in_costs_page',
    });
    props.goToDashboard();
  };

  const onSubmit = async (triggerSource: string) => {
    setIsLoading(true);

    const paymentDate = new Date(selectedDate);
    const payload: ScheduleMoveInCostsWebApiRequestDto = {
      paymentDate: {
        year: paymentDate.getFullYear(),
        month: paymentDate.getMonth() + 1,
        day: paymentDate.getDate(),
      },
      groupKey: moveInCostsDetails.groupKey,
      encodedPaymentMethodId: selectedPaymentMethodEncodedId,
    };

    const response = await props.onSubmit(payload);
    const { error } = response;
    if (error) {
      setIsLoading(false);
    } else {
      if (fromDashboard) {
        RachelCSEvents.emitClickSendFromMoveInCostsPageDashboardFlowEvent({
          user,
          trigger_source: triggerSource,
        });
      } else {
        RachelCSEvents.emitClickContinueFromMoveInCostsPageEvent({
          user,
          currentProperty: { listingAlias },
        });
      }
    }
  };

  const renderDatePicker = () => {
    const getLastSelectableDate = getLastSelectablePaymentDate(moveInCostsDetails.dueDate);
    const minDate = new Date();
    minDate.setDate(new Date().getDate() - 1);
    return (
      <FormField
        css={{ width: '100%' }}
        description={isPaymentDateAfterDueDate() ? <FormHelp error>{overDueText}</FormHelp> : null}
      >
        <DatePicker
          closeModalOnDateSelect
          dateFormat="MM/dd/yyyy"
          disabledDates={(date: Date) => date < minDate || date > getLastSelectableDate}
          onOpenChange={(open) => setIsCalendarOpen(open)}
          open={isCalendarOpen}
          input={
            <DateInput
              css={{ paddingLeft: 'sm !important', paddingTop: 'xs !important', paddingBottom: 'xs !important' }}
              value={selectedDate}
              readOnly
              onClick={() => setIsCalendarOpen(true)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  setIsCalendarOpen(true);
                }
              }}
            />
          }
          trigger={
            <TextButton tone="neutral">
              <Icon size="sm" render={<IconCalendarFilled />} />
            </TextButton>
          }
          onValueChange={(value) => changeSelectedDate(String(value))}
          value={selectedDate}
        />
      </FormField>
    );
  };

  const renderCalendarTitle = () => {
    if (isMICPaid) {
      return <Text>Your move-in payment was sent</Text>;
    } else if (failedPaymentEmail) {
      return (
        <Text css={{ color: 'red.500' }} textStyle="body-sm" className="ptm">
          We couldn&apos;t process your payment using your payment method. Contact your bank, or try a different payment
          method.
        </Text>
      );
    } else if (isOverdue()) {
      return <Text css={{ color: 'red.500' }}>Your move-in payment is overdue</Text>;
    } else {
      return renderDatePicker();
    }
  };

  const isPaymentDateAfterDueDate = () => {
    const left = coerceToDate(selectedDate);
    const right = coerceToDate(moveInCostsDetails.dueDate);
    return !!left && !!right && isAfter(left, right) && !isOverdue();
  };

  const renderMoveInCostsSection = (moveInCostsDetails: RenterMoveInCostsDetailsInterface) => {
    let zgFee = moveInCostsDetails.zgFee;
    let totalAmount = moveInCostsDetails.totalAmount;

    const selectedPaymentMethodDetails = paymentMethods.find(
      (paymentMethod) => paymentMethod.id === selectedPaymentMethodEncodedId,
    );

    const oneTimePaymentDetails = selectedPaymentMethodDetails?.oneTimePaymentAmountDetails.find(
      (oneTimePayment) => oneTimePayment.groupKey === moveInCostsDetails.groupKey,
    );

    if (oneTimePaymentDetails) {
      zgFee = oneTimePaymentDetails.fee;
      totalAmount = oneTimePaymentDetails.totalAmount;
    }

    return (
      <Card.Root elevated={false}>
        <Card.Header css={{ padding: 'token(spacing.sm) !important' }}>
          <Paragraph textStyle="body-bold">Move-in payment</Paragraph>
          <Paragraph className="fs-exclude">Due on {formatDate(moveInCostsDetails.dueDate, 'MMMM d, yyyy')}</Paragraph>
          {isMICPaid && <Tag tone="success">Paid</Tag>}
        </Card.Header>
        <Card.Body css={{ padding: 'token(spacing.sm) !important' }}>
          <MoveInCostsItemsDisplay zgFee={zgFee} totalAmount={totalAmount} items={moveInCostsDetails.items} />
        </Card.Body>
      </Card.Root>
    );
  };

  const handleContinueButtonClick = () => {
    if (!fromDashboard) {
      RachelCSEvents.emitClickContinueFromMoveInCostsPageEvent({
        user,
        currentProperty: { listingAlias },
      });
    }
    goToPaymentSettingPage();
  };

  const renderNavButtons = () => {
    const left = coerceToDate(selectedDate);
    const right = coerceToDate(formatDate(today, 'yyyy-MM-dd'));
    const isSelectedDateToday = !!left && !!right ? isEqual(left, right) : false;
    let paymentButtonText;
    let triggerSource;
    if (failedPaymentEmail) {
      paymentButtonText = 'Try again';
      triggerSource = 'try_again_button';
    } else if (isOverdue() || isSelectedDateToday) {
      paymentButtonText = 'Send move-in payment now';
      triggerSource = 'send_now_button';
    } else {
      paymentButtonText = 'Schedule move-in payment';
      triggerSource = 'schedule_button';
    }
    return (
      <>
        {!fromDashboard && <Button onClick={goToDashboard}>Go to dashboard</Button>}
        {fromDashboard && <TextButton onClick={() => setShowMultiplePaymentMethodSelectorModal(true)}>Back</TextButton>}
        {isMICPaid || isScheduleDateUnchanged() ? (
          <Button emphasis="filled" onClick={handleContinueButtonClick}>
            Continue
          </Button>
        ) : (
          <DebouncedButton emphasis="filled" onClick={() => onSubmit(triggerSource)} loading={isLoading}>
            {paymentButtonText}
          </DebouncedButton>
        )}
      </>
    );
  };

  const renderFaqSection = () => <FaqComponent faqs={faqs.MOVE_IN_COSTS_SCHEDULING_PAGE.body()} />;

  const renderMultiplePaymentMethodSelectorModal = () => {
    return (
      <Modal
        open={true}
        trigger={null}
        size="md"
        header={
          <Box css={{ padding: '16px 48px 16px 16px !important' }}>
            <Heading level={2} textStyle="heading-xs">
              Select a payment method
            </Heading>
          </Box>
        }
        onOpenChange={(isOpen) => {
          if (!isOpen) {
            goToDashboard();
          }
        }}
        body={
          <>
            <Box css={{ padding: '16px 48px 16px 16px !important' }}>
              <AvailablePaymentMethods
                onPaymentMethodClick={(paymentMethod) => setSelectedPaymentMethodEncodedId(paymentMethod.id)}
                renderRadioView={true}
                selectedPaymentMethodId={selectedPaymentMethodEncodedId}
              />
            </Box>
            <Box css={{ padding: '0 token(spacing.sm) token(spacing.sm) token(spacing.sm) !important' }}>
              <TextButton onClick={onAddNewPaymentClick}>Add new payment method</TextButton>
            </Box>
          </>
        }
        footer={
          <Box css={{ padding: 'token(spacing.sm) !important' }}>
            <ButtonGroup aria-label="Modal actions" role="group">
              <TextButton textStyle="body-bold" onClick={goToDashboard}>
                Back
              </TextButton>
              <Button
                disabled={!selectedPaymentMethodEncodedId}
                emphasis="filled"
                onClick={() => {
                  RachelCSEvents.emitClickContinueFromSelectPaymentMethodModalEvent({ user });
                  selectedPaymentMethodEncodedId && setShowMultiplePaymentMethodSelectorModal(false);
                }}
              >
                Continue
              </Button>
            </ButtonGroup>
          </Box>
        }
      />
    );
  };

  const renderMainSection = () => (
    <Box css={{ display: 'flex', flexDirection: 'column', gap: 'token(spacing.md)' }}>
      <NotificationContainer persona={userPersonas.RENTER} />
      <Box css={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 'token(spacing.sm)' }}>
        <Calendar title={isMICPaid ? 'Paid on' : 'Pay on'} dateLine1={formatDate(selectedDate, 'MMM do')} />
        {renderCalendarTitle()}
      </Box>
      <Box>
        <Text textStyle="body-sm">If this date falls on a weekend or holiday, we&apos;ll send the payment&nbsp;</Text>
        <Text textStyle="body-sm-bold">1 business day later.</Text>
      </Box>
      {renderMoveInCostsSection(moveInCostsDetails)}
      {!fromDashboard && (
        <ButtonGroup fluid aria-label="action-buttons" role="group">
          {renderNavButtons()}
        </ButtonGroup>
      )}
      <Box
        css={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 'token(spacing.xs)',
          marginBottom: 'token(spacing.md) !important',
        }}
      >
        <Paragraph textStyle="body-bold">Did you pay this offline?</Paragraph>
        <Paragraph css={{ color: 'text.subtle', textAlign: 'center' }}>
          If you&apos;ve taken care of this payment, please contact your landlord to cancel it.
        </Paragraph>
      </Box>
    </Box>
  );

  const renderHeader = () => (
    <Heading level={2} textStyle="heading-xs">
      {isMICPaid ? 'Your move-in payment has been taken care of.' : 'When would you like to make your move-in payment?'}
    </Heading>
  );

  const renderPage = () => {
    const progressBarProps = { progressTitle: MOVE_IN_COSTS_PAYMENT };
    return (
      <Page {...progressBarProps}>
        <HelmetContainer {...SeoConstants.moveInCostsSchedulingPage} />
        <Page.MainSection>
          <Box css={{ marginBottom: 'token(spacing.sm) !important' }}>{renderHeader()}</Box>
          {renderMainSection()}
        </Page.MainSection>
        <Page.RightSection>{renderFaqSection()}</Page.RightSection>
        <Page.BottomSection>{renderFaqSection()}</Page.BottomSection>
      </Page>
    );
  };

  const renderModal = () => {
    return (
      <Modal
        trigger={null}
        open={true}
        size="sm"
        onOpenChange={(isOpen) => {
          if (!isOpen) {
            goToDashboard();
          }
        }}
        header={<Box css={{ padding: '16px 48px 16px 16px !important' }}>{renderHeader()}</Box>}
        body={
          <Box css={{ padding: 'token(spacing.sm) !important' }}>
            <HelmetContainer {...SeoConstants.paymentInfoPage} />
            {renderMainSection()}
            {renderFaqSection()}
          </Box>
        }
        footer={
          <Box css={{ padding: 'token(spacing.sm) !important' }}>
            <ButtonGroup aria-label="action-buttons" role="group">
              {renderNavButtons()}
            </ButtonGroup>
          </Box>
        }
      />
    );
  };

  if (fromDashboard) {
    if (showMultiplePaymentMethodSelectorModal) {
      return renderMultiplePaymentMethodSelectorModal();
    }

    return renderModal();
  }

  return renderPage();
};

export default MoveInCostsSchedulingPage;
